import { api } from './client'
import type { Drill } from './receivables'

/**
 * Blocco 1 / Card D (REQ-162, 163, 164, 165, 167, 182, 199, 200, 201, 202, 210) — conto economico gestionale, report per
 * operatore / pratica / agente, cruscotto amministrativo e snapshot mensili. Forme pinnate nel brief. Le imposte portano
 * sempre l'etichetta STIMATO. Senza `CanSeeRealAmounts` le chiavi degli importi sono assenti: i campi sono opzionali.
 */

export interface DrillValue {
  value?: number | null
  drill?: Drill | null
}

export interface IncomeStatement {
  from?: string
  to?: string
  /** `false` con un settore: le spese non hanno un settore, quindi il risultato è il margine di contribuzione. */
  costsAllocated?: boolean
  /** R3-17: con un settore e il flag acceso, i costi comuni ripartiti sono inclusi (`sharedCosts`). */
  sharedCostsIncluded?: boolean
  sharedCosts?: number
  revenues?: {
    total: number
    byDepartment: { departmentId: number | null; name?: string; amount: number; commissions?: number; margin?: number; drill?: Drill | null }[]
  }
  directCosts?: { commissions: number; lead: number; variable: number; other: number }
  contributionMargin?: number
  operatingCosts?: { categoryId: number | null; name: string; amount: number }[]
  ebitda?: number
  marginPct?: number | null
  /** R4-03 (flag finanza.dimensioni_estese): incassi bancari per categoria di ricavo, solo a livello azienda; non entrano nei ricavi. */
  bankRevenueByIncomeCategory?: { categoryId: number | null; name: string; amount: number; movements: number }[] | null
}

export interface OperatorReportRow {
  operatorId: number
  name: string
  production?: number
  expected?: number
  accrued?: number
  collected?: number
  residual?: number
  avgDelayDays: number | null
  drill?: Drill | null
  reversals?: number
  commissions?: number
  margin?: number
}

export interface PracticeMargin {
  contractId: number
  operatorId?: number | null
  productId?: number | null
  agentUserId?: number | null
  departmentId?: number | null
  drill?: Drill | null
  revenue?: number
  leadCost?: number | null
  setter?: number
  agentCommission?: number
  overrides?: number
  otherDirect?: number
  margin?: number
}

export interface PracticeMarginList {
  items: PracticeMargin[]
  total: number
  /** Totali dell'intero elenco filtrato (assenti senza importi reali): coincidono con la riga da cui si arriva. */
  totals?: { revenue: number; agentCommission: number; margin: number } | null
}

/** R3-17: filtri dell'elenco pratiche, quelli del drill-down delle righe dei report. */
export interface PracticeFilters {
  operatorId?: string | undefined
  agentUserId?: string | undefined
  departmentId?: string | undefined
  productId?: string | undefined
  noDepartment?: boolean | undefined
}

export interface AgentMarginRow {
  agentUserId: number
  name?: string
  revenue?: number
  commissions?: number
  leadCost?: number | null
  /** `null` = costo del call center non configurato (DEC-006). */
  callCenterCost?: number | null
  reversals?: number
  margin?: number
  drill?: Drill | null
}

export interface AdminDashboard {
  /** Senza visibilità degli importi la risposta contiene solo questo. */
  restricted?: boolean
  asOf?: string
  /** `available=false`: conti non risponde, le cifre che ne dipendono hanno `value=null`. */
  treasury?: { available: boolean; reason?: string | null }
  bank?: DrillValue
  cash?: DrillValue
  receivables?: DrillValue
  payables?: DrillValue
  freeLiquidity?: DrillValue
  forecastRevenue?: DrillValue
  accrued?: DrillValue
  collected?: DrillValue
  costsMonth?: DrillValue
  margin?: DrillValue
  marginPct?: DrillValue
  taxes?: { amount?: DrillValue | number | null; label: string }
  in30?: DrillValue
  out30?: DrillValue
  runwayMonths?: DrillValue
  burnRate?: DrillValue
  budgetVsActual?: DrillValue
  partners?: DrillValue
  /** IMP-3: riconosciuto dalla mandante nell'ultimo mese di report. NON e' incassato: riga distinta. */
  recognizedByMandante?: {
    periodMonth: string | null
    net: number | null
    recurring: number | null
    total: number | null
    queue: number
    drill: Drill
  }
}

export interface SnapshotInfo {
  id?: number
  year: number
  month: number
  revision: number
  createdAt: string
  createdBy: number | null
}

export interface SnapshotDetail extends SnapshotInfo {
  /** Assente per chi non vede gli importi reali. */
  payload?: Record<string, unknown>
}

export type MarginDimension = 'prodotto' | 'pratica' | 'cliente' | 'operatore' | 'settore' | 'agente' | 'team' | 'callcenter' | 'fornitore_lead' | 'gara' | 'campagna' | 'fornitore'

export interface MarginDefinition { key: string; label: string; formula: string }

export interface MarginLevelsTotals {
  revenue: number
  commissions: number
  leadCost: number
  /** Compensi di attività maturati (setter, team leader, altri). */
  payoutQuotas?: number
  practice: number
  unattributedCommercialCosts: number
  commercial: number
  structureCosts: number
  management: number
  /** Stima da conti; `null` se conti non risponde. */
  estimatedTaxes: number | null
  corporate: number | null
}

export interface MarginLevelsRow {
  key: number | null
  name: string
  revenue: number
  commissions: number
  leadCost: number
  payoutQuotas?: number
  practice: number
  commercial: number
  management: number
  corporate: number | null
  drill?: { href: string } | null
}

export interface MarginLevels {
  from: string
  to: string
  dimension: MarginDimension
  definitions: MarginDefinition[]
  /** Vero per chi non vede gli importi reali: restano le sole definizioni. */
  restricted?: boolean
  taxesLabel?: string
  costOnly?: boolean
  levels?: MarginLevelsTotals
  rows?: (MarginLevelsRow | { key: number | null; name: string; cost: number })[]
}

export interface CustomerValueMonth {
  month: string
  revenue: number
  recurring: number
  commissions: number
  acquisitionCost: number
  value: number
}

export interface CustomerValue {
  contactId: number
  canSeeAmounts: boolean
  definition: string
  contracts?: number
  months?: CustomerValueMonth[]
  totals?: { revenue: number; recurring: number; commissions: number; acquisitionCost: number; value: number }
}

export interface ReportRange {
  from?: string
  to?: string
  departmentId?: string
}

function qs(params: object): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** Le liste non sono pinnate come array o `{ items }`: si accettano entrambe. */
const items = <T,>(r: T[] | { items: T[] }): T[] => (Array.isArray(r) ? r : r.items)

export const financeReportsApi = {
  incomeStatement: (r: ReportRange) => api<IncomeStatement>(`/api/finance/income-statement${qs(r)}`),
  operators: async (r: Omit<ReportRange, 'departmentId'>) =>
    items(await api<OperatorReportRow[] | { items: OperatorReportRow[] }>(`/api/finance/reports/operators${qs(r)}`)),
  practiceMargins: (r: Omit<ReportRange, 'departmentId'> & PracticeFilters & { page?: number; pageSize?: number }) =>
    api<PracticeMarginList>(`/api/finance/reports/practice-margins${qs(r)}`),
  practiceMargin: (contractId: number) => api<PracticeMargin>(`/api/finance/reports/practice-margins/${contractId}`),
  /** Solo Admin con `CanSeeRealAmounts`: gli altri ricevono 403. */
  agentsMargin: async (r: Omit<ReportRange, 'departmentId'>) =>
    items(await api<AgentMarginRow[] | { items: AgentMarginRow[] }>(`/api/finance/reports/agents-margin${qs(r)}`)),
  /** C-07, dietro `finanza.margini_livelli`. Agente: solo Admin. */
  marginLevels: (r: Omit<ReportRange, 'departmentId'> & { dimension: MarginDimension }) =>
    api<MarginLevels>(`/api/finance/reports/margin-levels${qs(r)}`),
  customerValue: (contactId: number) => api<CustomerValue>(`/api/finance/customer-value/${contactId}`),
  adminDashboard: () => api<AdminDashboard>('/api/finance/admin-dashboard'),
  snapshots: async () => items(await api<SnapshotInfo[] | { items: SnapshotInfo[] }>('/api/finance/snapshots')),
  snapshot: (year: number, month: number) => api<SnapshotDetail>(`/api/finance/snapshots/${year}/${month}`),
}
